import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SiteHeader from './SiteHeader';
import Footer from './Footer';

const CTA_BUTTON_STYLE = {
  display: 'inline-block',
  background: 'linear-gradient(135deg, #8B5FBF, #2c2e96)',
  color: '#fff',
  padding: '14px 32px',
  borderRadius: '25px',
  textDecoration: 'none',
  fontSize: '1rem',
  fontWeight: '600',
  boxShadow: '0 2px 10px rgba(139, 95, 191, 0.2)',
};

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

// Splits OkGather's own upcoming events (page.okgather_events, see app.py's
// _get_okgather_events_for_city) into what's soon (kept at the top, in the
// section's original position) vs further out (moved to its own section at
// the very bottom of the page, after the third-party list) - a long list of
// real RSVP-able events shouldn't be buried by a "from around the web"
// section further down, but something a month away doesn't need the same
// prominence as this week's either. An event with no confirmed starts_at
// (shouldn't normally happen - see _get_okgather_events_for_city) is kept
// in the "soon" bucket rather than silently dropped or misfiled.
const SOON_WINDOW_DAYS = 14;
function splitOkgatherEventsByDate(okgatherEvents) {
  const cutoff = Date.now() + SOON_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const soon = [];
  const later = [];
  okgatherEvents.forEach((event) => {
    if (event.starts_at && new Date(event.starts_at).getTime() > cutoff) {
      later.push(event);
    } else {
      soon.push(event);
    }
  });
  return { soon, later };
}

// Shared card markup for both OkGather-event sections (soon/later) below -
// identical styling either way, only the surrounding heading differs.
function renderOkgatherEventCards(list) {
  return (
    <ol style={{ padding: 0, listStyle: 'none' }}>
      {list.map((event, i) => (
        <li key={i} style={{
          border: '1px solid rgba(139, 95, 191, 0.25)', borderRadius: '10px',
          padding: '14px 16px', marginBottom: '10px', backgroundColor: '#faf7ff'
        }}>
          <p style={{ margin: '0 0 4px 0' }}>
            <span style={{
              display: 'inline-block', fontSize: '0.7rem', fontWeight: '700', color: '#6B46C1',
              background: '#ede4fb', borderRadius: '4px', padding: '1px 6px', marginRight: '8px'
            }}>
              OKGATHER
            </span>
            <strong style={{ fontSize: '1.05rem', color: '#1F2937' }}>{event.name}</strong>
            {event.date ? ` — ${event.date}` : ''}
          </p>
          {event.description && (
            <p style={{ margin: '0 0 6px 0', color: '#333', lineHeight: '1.6' }}>{event.description}</p>
          )}
          <a href={event.source_url} style={{ color: '#6B46C1', fontWeight: '600', textDecoration: 'none' }}>
            View on OkGather &rarr;
          </a>
        </li>
      ))}
    </ol>
  );
}

// Mirrors app.py's serve_local_events_page - same heading/intro copy for
// the evergreen per-city URL vs a dated weekly archive page.
function pageCopy(page, eventCount) {
  if (page.is_evergreen) {
    return {
      heading: `Best ${page.city} Events for 20s & 30s`,
      intro: `Looking for the best ${page.city} events for meeting new people in your 20s and 30s? This page rounds up what's on in ${page.city} each week - handpicked events for getting out, trying something new, and making friends, updated regularly.`,
    };
  }
  return {
    heading: `${eventCount} Things to Do in ${page.city} This Week`,
    intro: `Looking for things to do in ${page.city} this week or this weekend? Here's a handpicked list of ${eventCount} local events in ${page.city} for people who want to get out, try something new, and meet new people.`,
  };
}

function LocalEventsPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`/api/public/event-pages/${slug}`)
      .then((response) => {
        if (!response.ok) throw new Error(response.status === 404 ? 'Page not found' : 'Failed to load page');
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        setPage(data);
        // Client-side fallback for in-app navigation - the real crawler-facing
        // meta tags come from the server-rendered shell (see app.py's
        // serve_local_events_page), this just keeps the tab title/description
        // correct if someone navigates here without a full page load.
        const { heading, intro } = pageCopy(data, (data.events || []).length);
        document.title = `${heading} | OkGather`;
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
          metaDescription.setAttribute('content', intro);
        }
      })
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p>Loading...</p>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="page-wrapper" style={{ minHeight: '100vh', backgroundColor: '#FAFAFA' }}>
        <SiteHeader />
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '60px 20px', textAlign: 'center' }}>
          <h1>{error === 'Page not found' ? 'Page not found' : 'Oops!'}</h1>
          <Link to="/" style={{ color: '#6B46C1', textDecoration: 'none', fontWeight: '600' }}>
            &larr; Back to OkGather
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const events = page.events || [];
  const okgatherEvents = page.okgather_events || [];
  const { soon: soonOkgatherEvents, later: laterOkgatherEvents } = splitOkgatherEventsByDate(okgatherEvents);
  const { heading, intro } = pageCopy(page, events.length);

  return (
    <div className="page-wrapper" style={{ minHeight: '100vh', backgroundColor: '#FAFAFA' }}>
      <SiteHeader />
      <main className="main-wrapper">
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '30px 20px' }}>
          <p style={{ fontSize: '0.9rem', color: '#6B7280', marginBottom: '24px' }}>
            <Link to="/" style={{ color: '#6B46C1', textDecoration: 'none' }}>Home</Link>
            {' / '}
            <span>{page.city} Events</span>
          </p>

          <header style={{ marginBottom: '20px' }}>
            <h1 style={{ margin: '0 0 10px 0', color: '#1F2937', fontSize: '2.25rem', fontWeight: '800', lineHeight: '1.2' }}>
              {heading}
            </h1>
            <p style={{ color: '#6B7280', fontSize: '0.95rem', margin: 0 }}>
              {formatDate(page.week_start)} to {formatDate(page.week_end)}
            </p>
          </header>

          <p style={{ color: '#374151', fontSize: '1rem', lineHeight: '1.6', marginBottom: '28px' }}>
            {intro}
          </p>

          <div style={{
            background: '#f0fdf4', border: '1px solid rgba(16, 185, 129, 0.25)', borderRadius: '12px',
            padding: '20px 24px', marginBottom: '36px', textAlign: 'center',
          }}>
            <p style={{ margin: '0 0 14px 0', color: '#1F2937', fontWeight: '600' }}>
              Enjoy meeting new people at things like this? OkGather runs regular social gatherings in {page.city}.
            </p>
            <Link to={`/events?local_events_ref=${encodeURIComponent(slug)}`} style={CTA_BUTTON_STYLE}>
              Browse Gatherings
            </Link>
          </div>

          {soonOkgatherEvents.length > 0 && (
            <section style={{ marginBottom: '36px' }}>
              <h2 style={{ fontSize: '1.15rem', fontWeight: '800', color: '#1F2937', marginBottom: '4px' }}>
                Upcoming OkGather events in {page.city}
              </h2>
              <p style={{ fontSize: '0.85rem', color: '#6B7280', marginBottom: '14px' }}>
                Our own gatherings, not from around the web - RSVP directly on OkGather.
              </p>
              {renderOkgatherEventCards(soonOkgatherEvents)}
            </section>
          )}

          <section>
            <h2 style={{ fontSize: '1.15rem', fontWeight: '800', color: '#1F2937', marginBottom: '4px' }}>
              What's on in {page.city} (from around the web)
            </h2>
            <p style={{ fontSize: '0.85rem', color: '#6B7280', marginBottom: '14px' }}>
              Third-party events curated for people who want to get out and meet new people - not run by OkGather.
            </p>
            {events.length === 0 ? (
              <p style={{ color: '#666' }}>No events listed.</p>
            ) : (
              <ol style={{ padding: 0, listStyle: 'none' }}>
                {events.map((event, i) => (
                  <li key={i} style={{ borderTop: '1px solid #eee', padding: '18px 0' }}>
                    <p style={{ margin: '0 0 6px 0', fontWeight: '700', fontSize: '1.05rem', color: '#1F2937' }}>
                      {i + 1}. {event.name}{event.date ? ` — ${event.date}` : ''}
                    </p>
                    {event.description && (
                      <p style={{ margin: '0 0 6px 0', color: '#333', lineHeight: '1.6' }}>{event.description}</p>
                    )}
                    {event.source_url && (
                      <a href={event.source_url} target="_blank" rel="noopener noreferrer" style={{ color: '#6B46C1', fontWeight: '600', textDecoration: 'none' }}>
                        More info &rarr;
                      </a>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </section>

          {laterOkgatherEvents.length > 0 && (
            <section style={{ marginTop: '36px' }}>
              <h2 style={{ fontSize: '1.15rem', fontWeight: '800', color: '#1F2937', marginBottom: '4px' }}>
                More OkGather events coming up in {page.city}
              </h2>
              <p style={{ fontSize: '0.85rem', color: '#6B7280', marginBottom: '14px' }}>
                A bit further out ({SOON_WINDOW_DAYS}+ days away) - RSVP directly on OkGather.
              </p>
              {renderOkgatherEventCards(laterOkgatherEvents)}
            </section>
          )}

          <div style={{ textAlign: 'center', marginTop: '36px', paddingTop: '24px', borderTop: '1px solid #eee' }}>
            <Link to="/signup" style={CTA_BUTTON_STYLE}>Sign up to OkGather</Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default LocalEventsPage;
